import type { ReactNode } from "react";
import { useEffect, useMemo, useState } from "react";
import type { RoleSerializerWithPermissions } from "../api-client/models/roleSerializerWithPermissions";
import { rolesList } from "../api-client/roles/roles";
import { createPermissionService, getCachedRoles, setCachedRoles } from "./permissionServiceCore";
import { PermissionServiceContext } from "./permissionServiceContext";

let rolesPromise: Promise<RoleSerializerWithPermissions[]> | undefined;

const ensureRoles = () => {
  if (!rolesPromise) {
    rolesPromise = rolesList()
      .then((response) => {
        setCachedRoles(response);
        return response;
      })
      .catch((error) => {
        rolesPromise = undefined;
        throw error;
      });
  }
  return rolesPromise;
};

export const PermissionProvider = ({ children }: { children: ReactNode }) => {
  const [roles, setRoles] = useState<RoleSerializerWithPermissions[]>(getCachedRoles());

  useEffect(() => {
    let isMounted = true;
    ensureRoles()
      .then((loadedRoles) => {
        if (isMounted) {
          setRoles(loadedRoles);
        }
      })
      .catch(() => {
        if (isMounted) {
          setRoles([]);
        }
      });

    return () => {
      isMounted = false;
    };
  }, []);

  const value = useMemo(
    () => createPermissionService(() => roles),
    [roles],
  );

  return <PermissionServiceContext.Provider value={value}>{children}</PermissionServiceContext.Provider>;
};
